import { useTranslation } from 'react-i18next'
import { StageGlyph } from '@/components/app/indicators/StageGlyph'

interface MockRow {
  id: string
  titleKey: string
  high?: boolean
  overdue?: boolean
  ring: 'progress' | 'todo'
}

const IN_PROGRESS: Array<MockRow> = [
  { id: 'MOL-1', titleKey: 'preview.task1', high: true, ring: 'progress' },
  { id: 'MOL-4', titleKey: 'preview.task2', ring: 'progress' },
  { id: 'MOL-7', titleKey: 'preview.task3', high: true, ring: 'progress' },
]
const BACKLOG: Array<MockRow> = [
  { id: 'MOL-12', titleKey: 'preview.task4', overdue: true, ring: 'todo' },
]

/** The app's stage glyph for a mock row: half ring for "in progress", dashed for to-do. */
export function StatusRing({ variant = 'todo', className }: { variant?: 'todo' | 'progress'; className?: string }) {
  return <StageGlyph stage={variant === 'progress' ? 'active' : 'backlog'} className={className} />
}

function Row({ row }: { row: MockRow }) {
  const { t } = useTranslation('auth')
  return (
    <div className="flex items-center gap-3 border-t border-border/70 px-4 py-2.5 text-[13px]">
      <StatusRing variant={row.ring} />
      <span className="w-14 shrink-0 font-mono text-[11px] text-muted-foreground">{row.id}</span>
      <span className="min-w-0 flex-1 truncate">{t(row.titleKey)}</span>
      {row.high ? <span className="shrink-0 text-xs font-medium text-orange-600 dark:text-orange-400">{t('preview.high')}</span> : null}
      {row.overdue ? <span className="shrink-0 text-xs font-medium text-brand">{t('preview.overdue')}</span> : null}
    </div>
  )
}

/**
 * Static product preview for the sign-in split (design turn 12b): a tasks
 * card and an overlapping doc card with an embedded task chip. Purely
 * decorative — the parent <aside> is aria-hidden.
 */
export function AuthLoginPreview() {
  const { t } = useTranslation('auth')
  return (
    <div className="flex h-full flex-col justify-between gap-10 p-10 xl:p-14">
      <div className="relative mx-auto mt-6 w-full max-w-[520px] pb-24">
        <div className="overflow-hidden rounded-xl bg-card text-card-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_-12px_rgba(90,40,20,0.25)]">
          <div className="flex items-center gap-2 px-4 py-3.5">
            <span className="text-sm font-semibold">{t('preview.project')}</span>
            <span className="text-sm text-muted-foreground">/</span>
            <span className="text-sm text-muted-foreground">{t('preview.view')}</span>
            <span className="ml-auto rounded-full bg-brand-tint px-2 py-0.5 text-[11px] font-medium text-brand-tint-foreground">
              {t('preview.openCount')}
            </span>
          </div>
          <p className="bg-chrome px-4 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {t('preview.inProgress')}
          </p>
          {IN_PROGRESS.map((row) => (
            <Row key={row.id} row={row} />
          ))}
          <p className="border-t border-border/70 bg-chrome px-4 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {t('preview.backlog')}
          </p>
          {BACKLOG.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </div>

        <div className="absolute -bottom-2 right-0 w-[62%] rounded-xl bg-card p-4 text-card-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_40px_-12px_rgba(90,40,20,0.3)] xl:-right-6">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('preview.docEyebrow')}</p>
          <p className="mt-1 text-[15px] font-semibold">{t('preview.docTitle')}</p>
          <div className="mt-3 space-y-1.5">
            <div className="h-2 w-full rounded-full bg-secondary" />
            <div className="h-2 w-4/5 rounded-full bg-secondary" />
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-xs">
            <StatusRing />
            <span className="font-mono text-[10px] text-muted-foreground">MOL-9</span>
            <span className="truncate">{t('preview.docTask')}</span>
          </div>
        </div>
      </div>

      <div className="max-w-sm space-y-1">
        <p className="text-[15px] font-semibold text-foreground">{t('preview.captionTitle')}</p>
        <p className="text-sm text-brand-tint-foreground">{t('preview.captionBody')}</p>
      </div>
    </div>
  )
}
